import type { MNHotInstance } from '../tableHotInstanceTypes';

import { getEditorMinHeight } from '../utils/hotInstanceUtils/cell/hotTableCellEditorHeightUtils';
import { getEditorWidth } from '../utils/hotInstanceUtils/cell/hotTableCellEditorWidthUtils';
import { toCssPx } from '../../../utils/cssUtil';

type EditedCellRect = {
    start: number;
    top: number;
};

type MilanoteCellEditorLayoutManagerArgs = {
    hotInstance: MNHotInstance;
    onEditorHeightChange: (editorHeight: number | null) => void;
};

/**
 * Manages the layout of the cell editor
 *
 * - Initialise position and size
 * - Listens to editor size changes
 */
class MilanoteCellEditorLayoutManager {
    private readonly hotInstance: MNHotInstance;
    private readonly onEditorHeightChange: (editorHeight: number | null) => void;

    private editorHolderElement: HTMLElement | null = null;
    private editorPositionerElement: HTMLElement | null = null;

    private editorHeightValue: number | null = null;
    private editorHolderMinHeight: number | null = null;
    private editorHeightSyncFrameId: number | null = null;

    constructor({ hotInstance, onEditorHeightChange }: MilanoteCellEditorLayoutManagerArgs) {
        this.hotInstance = hotInstance;
        this.onEditorHeightChange = onEditorHeightChange;
    }

    /**
     * Stores the Handsontable editor DOM elements used by the layout manager.
     */
    initEditorElements(rootElement: HTMLElement) {
        this.editorPositionerElement = rootElement.querySelector<HTMLElement>('.handsontable-input-positioner');
        this.editorHolderElement =
            this.editorPositionerElement?.querySelector<HTMLElement>('.handsontable-input-holder') || null;

        return !!this.editorPositionerElement && !!this.editorHolderElement;
    }

    /**
     * Sets the editor background color CSS variable on the editor holder.
     */
    setBackgroundColor(backgroundColor: string) {
        if (!this.editorHolderElement) return;

        this.editorHolderElement.style.setProperty('--ws-table-cell-background-color', backgroundColor);
    }

    /**
     * Makes the prepared editor measurable without exposing it above the cell renderer.
     */
    prepareEditor() {
        if (!this.editorHolderElement) return;

        this.editorHolderElement.style.display = 'flex';
        this.editorHolderElement.style.visibility = 'hidden';
    }

    /**
     * Shows the editor holder while editing is active.
     */
    showEditor() {
        if (!this.editorHolderElement) return;

        this.editorHolderElement.style.display = 'flex';
        this.editorHolderElement.style.visibility = 'visible';
    }

    /**
     * Hides the editor holder once editing ends.
     */
    hideEditor() {
        if (!this.editorHolderElement) return;

        this.editorHolderElement.style.display = 'none';
        this.editorHolderElement.style.visibility = '';
    }

    /**
     * Positions and sizes the editor to match the active cell.
     */
    initEditorPositionAndSize(row: number, col: number, editedCellRect: EditedCellRect) {
        if (this.hotInstance.milanoteProps.isResizing) return;

        const nRows = this.hotInstance.countRows();

        this.editorHolderMinHeight = getEditorMinHeight(this.hotInstance, row, col);
        const editorWidth = getEditorWidth(this.hotInstance, row, col);

        if (!this.editorPositionerElement || !this.editorHolderElement) return;

        // When table title is showing, editedCellRect.top returns 1, and
        // when table title is not showing, editedCellRect.top returns -1.
        // Both of these should be normalised to 0.
        const editorPositionTop = editedCellRect.top === 1 || editedCellRect.top === -1 ? 0 : editedCellRect.top;

        this.editorPositionerElement.style.top = toCssPx(editorPositionTop);
        this.editorPositionerElement.style.left = toCssPx(editedCellRect.start);

        this.editorHolderElement.style.minHeight = toCssPx(this.editorHolderMinHeight);
        this.editorHolderElement.style.width = toCssPx(editorWidth);

        this.editorHolderElement.classList.remove('first-row');
        this.editorHolderElement.classList.remove('last-row');

        if (row === 0) this.editorHolderElement.classList.add('first-row');
        if (row === nRows - 1) this.editorHolderElement.classList.add('last-row');
    }

    /**
     * Measures the rendered editor height from the editor holder contents and syncs the tracked height.
     */
    syncEditorHeight() {
        if (this.editorHeightSyncFrameId !== null) {
            cancelAnimationFrame(this.editorHeightSyncFrameId);
        }

        // Use requestAnimationFrame to make sure we get the latest value of this.editorHolderElement.offsetHeight
        this.editorHeightSyncFrameId = requestAnimationFrame(() => {
            this.editorHeightSyncFrameId = null;

            if (!this.editorHolderElement || this.editorHolderMinHeight === null) return;

            const nextEditorHeight = Math.max(this.editorHolderElement.offsetHeight, this.editorHolderMinHeight);

            if (this.editorHeightValue === nextEditorHeight) return;

            this.editorHeightValue = nextEditorHeight;

            this.onEditorHeightChange(this.editorHeightValue);
        });
    }

    /**
     * Clears tracked editor state and rerenders once the editor closes.
     */
    clearTrackedEditorHeight() {
        if (this.editorHeightSyncFrameId !== null) {
            cancelAnimationFrame(this.editorHeightSyncFrameId);
            this.editorHeightSyncFrameId = null;
        }

        this.editorHeightValue = null;
        this.onEditorHeightChange(this.editorHeightValue);
    }

    get editorHeight() {
        return this.editorHeightValue;
    }
}

export default MilanoteCellEditorLayoutManager;
